// Config-driven CRUD list — 12+ modülde reuse edilir.
// createFields → Dialog + form (yeni kayıt)
// canDelete    → satır bazlı sil ikonu
// selectable   → checkbox + toplu sil
// canDeleteAll → "Tümünü Sil" toolbar butonu
// canEdit=true → düzenle ikonu; createFields ile aynı dialog reused
// summary      → üst kısımda özel özet çubuğu (JSX)
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Eye, RefreshCw, Loader2, Pencil, Trash, FileUp } from "lucide-react";
import { PageHeader, StatusPill } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmProvider";
import { ImportDialog } from "@/components/ImportDialog";

export function ListPage({
  title, subtitle, endpoint, columns, testId,
  createHref, onView, canDelete = false, canEdit = false,
  deleteConfirm = "Silinsin mi?",
  extraActions, extraRowActions, createFields, createTitle, createPayload, defaultSort,
  canDeleteAll = false, summary, listQuery, onDataChange, pdfSummary,
  importModule,
  importLabel,
  bulkExtras,  // opsiyonel: (selectedIds, clearSelection) => JSX — çoklu bulk action UI'ı
  // === Kullanıcı isteği: Ödenmiş kayıtları varsayılan olarak gizle, toggle ile aç ===
  // hidePaidByDefault: true ise `paidStatuses` içindeki durumları filtreler.
  // dateKey: sıralama için tarih alanı (due_date, payment_date, next_run vs)
  hidePaidByDefault = false,
  paidStatuses = ["odendi", "paid", "cleared", "tahsil_oldu"],
  dateKey = "due_date",
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pageSize, setPageSize] = useState(500);
  const [hasMore, setHasMore] = useState(false);
  const [showPaid, setShowPaid] = useState(false);  // varsayılan: ödenenleri gizle
  const nav = useNavigate();
  const confirm = useConfirm();

  const load = async (size = pageSize) => {
    setLoading(true);
    try {
      const q = listQuery ? `${listQuery}&limit=${size}` : `limit=${size}`;
      const url = `${endpoint}?${q}`;
      const { data } = await api.get(url);
      setRows(data);
      // Backend'in döndürdüğü kayıt sayısı istediğimiz limite eşitse muhtemelen daha fazlası var
      setHasMore(Array.isArray(data) && data.length >= size);
      if (onDataChange) onDataChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // Kullanıcı "Daha fazla göster"e basınca pageSize'ı 500 arttır
  const loadMore = () => {
    const next = pageSize + 500;
    setPageSize(next);
    load(next);
  };
  // Filtreler değişince sayfa boyutunu 500'e resetle (istenmeyen büyük fetch'i engelle)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPageSize(500);
    load(500);
  }, [endpoint, listQuery]);

  const handleDelete = async (row) => {
    const ok = await confirm({ title: "Kayıt Silinecek", message: deleteConfirm, confirmText: "Evet, Sil" });
    if (!ok) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleBulkDelete = async (ids) => {
    if (!ids || ids.length === 0) return;
    const ok = await confirm({
      title: "Toplu Silme",
      message: `Seçilen ${ids.length} kayıt kalıcı olarak silinecek. Onaylıyor musunuz?`,
      confirmText: `Evet, ${ids.length} kaydı sil`,
    });
    if (!ok) return;
    try {
      await api.post(`${endpoint}/bulk-delete`, { ids });
      toast.success(`${ids.length} kayıt silindi`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDeleteAll = async () => {
    const ok1 = await confirm({
      title: `⚠ DİKKAT — ${title}`,
      message: `${title} listesindeki TÜM kayıtlar silinecek. Bu işlem GERİ ALINAMAZ.\n\nDevam etmek istiyor musunuz?`,
      confirmText: "Anladım, devam et",
    });
    if (!ok1) return;
    const ok2 = await confirm({
      title: "Son Onay",
      message: `Emin misiniz? Tüm kayıtlar kalıcı olarak silinecek.`,
      confirmText: "Evet, tümünü sil",
    });
    if (!ok2) return;
    try {
      const { data } = await api.delete(endpoint);
      toast.success(`${data.deleted || 0} kayıt silindi`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openCreate = () => {
    if (createFields) setAddOpen(true);
    else if (createHref) nav(createHref);
  };

  // Ödenenleri gizle + Bugünden Geleceğe sırala (unpaid önce, tarih asc)
  const displayRows = useMemo(() => {
    if (!hidePaidByDefault) return rows;
    const paidSet = new Set(paidStatuses.map(s => String(s).toLowerCase()));
    const filtered = showPaid ? rows : rows.filter(r => !paidSet.has(String(r.status || "").toLowerCase()));
    return [...filtered].sort((a, b) => {
      const aPaid = paidSet.has(String(a.status || "").toLowerCase()) ? 1 : 0;
      const bPaid = paidSet.has(String(b.status || "").toLowerCase()) ? 1 : 0;
      if (aPaid !== bPaid) return aPaid - bPaid;  // ödenmemişler önce
      const ad = a[dateKey] || ""; const bd = b[dateKey] || "";
      return ad.localeCompare(bd);  // asc: bugünden geleceğe
    });
  }, [rows, hidePaidByDefault, showPaid, paidStatuses, dateKey]);

  return (
    <div data-testid={testId}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <>
            {extraActions}
            <Button variant="outline" size="sm" onClick={load} data-testid={`${testId}-refresh`}>
              <RefreshCw className="h-4 w-4 mr-2" /> Yenile
            </Button>
            {importModule && (
              <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}
                      data-testid={`${testId}-import-btn`}>
                <FileUp className="h-4 w-4 mr-2" /> İçe Aktar
              </Button>
            )}
            {canDeleteAll && rows.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleDeleteAll}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      data-testid={`${testId}-delete-all-btn`}>
                <Trash className="h-4 w-4 mr-2" /> Tümünü Sil
              </Button>
            )}
            {(createHref || createFields) && (
              <Button size="sm" onClick={openCreate} data-testid={`${testId}-create-btn`}>
                <Plus className="h-4 w-4 mr-2" /> Yeni
              </Button>
            )}
          </>
        }
      />

      {summary}

      <DataTable
        data={displayRows}
        columns={columns}
        title={title}
        loading={loading}
        testId={testId}
        initialSort={defaultSort}
        defaultPageSize={500}
        selectable={canDelete}
        onBulkAction={canDelete ? handleBulkDelete : undefined}
        pdfSummary={pdfSummary}
        bulkExtras={bulkExtras}
        // eslint-disable-next-line react/no-unstable-nested-components
        rowActions={(row) => (
          <div className="flex gap-1 justify-end">
            {extraRowActions && extraRowActions(row)}
            {onView && (
              <Button variant="ghost" size="icon" onClick={() => onView(row)} title="Görüntüle" data-testid={`${testId}-view-${row.id}`}>
                <Eye className="h-4 w-4" />
              </Button>
            )}
            {canEdit && createFields && (
              <Button variant="ghost" size="icon" onClick={() => setEditRow(row)} title="Düzenle" data-testid={`${testId}-edit-${row.id}`}>
                <Pencil className="h-4 w-4 text-blue-500" />
              </Button>
            )}
            {canDelete && (
              <Button variant="ghost" size="icon" onClick={() => handleDelete(row)} title="Sil" data-testid={`${testId}-delete-${row.id}`}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        )}
      />

      {hasMore && (
        <div className="flex justify-center py-4" data-testid={`${testId}-load-more-wrap`}>
          <Button variant="outline" onClick={loadMore} disabled={loading}
                  data-testid={`${testId}-load-more`}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin"/> : <RefreshCw className="h-4 w-4 mr-2"/>}
            Daha Fazla Göster (+100 kayıt)
          </Button>
        </div>
      )}

      {createFields && addOpen && (
        <CreateOrEditDialog
          title={createTitle || `Yeni ${title}`}
          fields={createFields}
          endpoint={endpoint}
          preparePayload={createPayload}
          testId={testId}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {createFields && editRow && (
        <CreateOrEditDialog
          title={`Düzenle`}
          fields={createFields}
          endpoint={endpoint}
          preparePayload={createPayload}
          testId={testId}
          existing={editRow}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); load(); }}
        />
      )}
      {importModule && importOpen && (
        <ImportDialog
          moduleSlug={importModule}
          moduleLabel={importLabel || title}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function CreateOrEditDialog({ title, fields, endpoint, preparePayload, testId,
                              onClose, onSaved, existing }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(() =>
    fields.reduce((acc, f) => {
      let initial = existing?.[f.key] ?? f.default ?? "";
      // Yeni kayıt oluştururken localStorage'da hatırlanan son değeri kullan.
      if (!isEdit && !existing && f.remember && !initial) {
        try {
          const arr = JSON.parse(localStorage.getItem(`hasyapi_remember_${f.remember}`) || "[]");
          if (Array.isArray(arr) && arr.length > 0) initial = arr[0];
        } catch { /* ignore */ }
      }
      return { ...acc, [f.key]: initial };
    }, {})
  );
  const [busy, setBusy] = useState(false);
  const [refData, setRefData] = useState({});

  // Hatırlanan alanlar için önceki değerlerin listesi (datalist için).
  const rememberedValues = useMemo(() => {
    const out = {};
    fields.forEach(f => {
      if (f.remember) {
        try {
          const arr = JSON.parse(localStorage.getItem(`hasyapi_remember_${f.remember}`) || "[]");
          out[f.key] = Array.isArray(arr) ? arr : [];
        } catch { out[f.key] = []; }
      }
    });
    return out;
  }, [fields]);

  useEffect(() => {
    const jobs = fields.filter(f => f.type === "select" && f.endpoint).map(async (f) => {
      try {
        const { data } = await api.get(f.endpoint);
        return [f.key, Array.isArray(data) ? data : []];
      } catch { return [f.key, []]; }
    });
    Promise.all(jobs).then(pairs => setRefData(Object.fromEntries(pairs)));
  }, [fields]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    for (const f of fields) {
      if (f.required && !String(form[f.key] ?? "").trim()) {
        toast.error(`${f.label} zorunlu`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload = preparePayload ? preparePayload(form) : form;
      if (isEdit) {
        await api.put(`${endpoint}/${existing.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await api.post(endpoint, payload);
        toast.success("Eklendi");
      }
      // Başarılı kayıt sonrası hatırlanan alanların değerlerini localStorage'a kaydet.
      fields.forEach(f => {
        if (!f.remember) return;
        const v = String(form[f.key] ?? "").trim();
        if (!v) return;
        try {
          const key = `hasyapi_remember_${f.remember}`;
          const prev = JSON.parse(localStorage.getItem(key) || "[]");
          const arr = Array.isArray(prev) ? prev.filter(x => x !== v) : [];
          arr.unshift(v);
          localStorage.setItem(key, JSON.stringify(arr.slice(0, 20)));
        } catch { /* ignore */ }
      });
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${testId}-${isEdit?'edit':'create'}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{isEdit ? "Kayıt bilgilerini güncelleyip Kaydet butonuna basın." : "Tüm zorunlu alanları doldurun ve Ekle butonuna basın."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fields.map((f) => {
            // remember alanı için önceki değerleri datalist'e ekle
            const mergedField = f.remember && rememberedValues[f.key]?.length
              ? { ...f, datalist: [...(f.datalist || []), ...rememberedValues[f.key]] }
              : f;
            return (
              <FieldRow key={f.key} field={mergedField} value={form[f.key]} onChange={(v) => set(f.key, v)}
                       options={refData[f.key] || f.options} testId={`${testId}-field-${f.key}`}/>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={busy} data-testid={`${testId}-save-btn`}>
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>}
            {isEdit ? "Kaydet" : "Ekle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ field, value, onChange, options, testId }) {
  const label = <Label className="text-xs mb-1 block">
    {field.label} {field.required && <span className="text-red-500">*</span>}
  </Label>;

  if (field.type === "select") {
    const opts = options || [];
    return (
      <div>
        {label}
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={testId}><SelectValue placeholder={field.placeholder || "Seçin"}/></SelectTrigger>
          <SelectContent>
            {opts.length === 0 && <SelectItem value="_empty" disabled>Kayıt yok</SelectItem>}
            {opts.map(o => (
              <SelectItem key={o.id || o.value || o.code} value={o.id || o.value || o.code}>
                {o.label || o.name || o.mandate_no || o.title || o.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.hint && <div className="text-xs text-muted-foreground mt-1">{field.hint}</div>}
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div>
        {label}
        <Textarea rows={2} value={value || ""} onChange={e => onChange(e.target.value)}
                  placeholder={field.placeholder} data-testid={testId}/>
      </div>
    );
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} data-testid={testId}/>
        <span>{field.label}</span>
      </label>
    );
  }
  const listId = field.datalist ? `${testId}-datalist` : undefined;
  return (
    <div>
      {label}
      <Input
        type={field.type || "text"}
        step={field.step}
        value={value ?? ""}
        onChange={e => onChange(e.target.value)}
        placeholder={field.placeholder}
        list={listId}
        data-testid={testId}
      />
      {field.datalist && field.datalist.length > 0 && (
        <datalist id={listId}>
          {field.datalist.map(v => <option key={v} value={v} />)}
        </datalist>
      )}
      {field.hint && <div className="text-xs text-muted-foreground mt-1">{field.hint}</div>}
    </div>
  );
}

export const statusCol = (key = "status") => ({
  key, header: "Durum",
  render: (r) => <StatusPill status={r[key]} />,
  align: "left",
});
